import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { queryKeys } from '../infrastructure/react-query/queryKeys';

export function useCarStatusSocket() {
  const queryClient = useQueryClient();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  useEffect(() => {
    if (!accessToken) return;

    const socket = io(process.env.REACT_APP_BACKEND_URL ?? '', {
      reconnectionDelay: 2000,
      // Autenticação do websocket (at-36): o backend valida este JWT no
      // handshake e deriva a sala do usuário do próprio token. O `emit('register')`
      // de compat com o backend antigo foi removido no at-38, depois do deploy
      // conjunto de 31/07/2026 — o payload autodeclarado nunca dá acesso a sala.
      auth: { token: accessToken },
    });

    socket.on('car:status-updated', (data: { carId: string; status: string; plate: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.all });
      window.dispatchEvent(new CustomEvent('car:status-updated', { detail: data }));
    });

    socket.on('car:assigned', (data: { carId: string; plate: string }) => {
      window.dispatchEvent(new CustomEvent('car:assigned', { detail: data }));
    });

    socket.on('car:unassigned', (data: { carId: string; plate: string }) => {
      window.dispatchEvent(new CustomEvent('car:unassigned', { detail: data }));
    });

    socket.on('work-session:pending-approval', (data: { carId: string; carPlate: string }) => {
      window.dispatchEvent(new CustomEvent('work-session:pending-approval', { detail: data }));
    });

    socket.on('work-session:resolved', (data: { carId: string; sessionId: string }) => {
      window.dispatchEvent(new CustomEvent('work-session:resolved', { detail: data }));
    });

    // O backend emite 'notification:car-released' na liberação administrativa; o evento de
    // janela 'notification:created' é o canal que as páginas já escutam para refazer o fetch.
    socket.on('notification:car-released', (data: {
      id: string;
      type: string;
      title: string;
      message: string;
      carId: string;
      carPlate: string;
      createdAt: string;
    }) => {
      toast.info(`Veículo ${data.carPlate} liberado pela administração.`, { autoClose: 6000 });
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.all });
      window.dispatchEvent(new CustomEvent('notification:created', { detail: data }));
    });

    // at-46: um serviço ficou inacabado numa esteira — pendência que o Chefe precisa ver.
    socket.on('notification:servico-inacabado', (data: {
      id: string;
      type: string;
      title: string;
      message: string;
      carId: string;
      carPlate: string;
      esteiraNome?: string;
      createdAt: string;
    }) => {
      toast.warning(
        `Serviço inacabado no veículo ${data.carPlate}${data.esteiraNome ? ` (${data.esteiraNome})` : ''}.`,
        { autoClose: 8000 },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.cars.all });
      window.dispatchEvent(new CustomEvent('notification:created', { detail: data }));
    });


    socket.on('notification:session-interrupted', (data: {
      id: string;
      type: string;
      title: string;
      message: string;
      carId: string;
      carPlate: string;
      createdAt: string;
    }) => {
      window.dispatchEvent(new CustomEvent('notification:session-interrupted', { detail: data }));
    });

    // at-48b: a configuração das janelas de pausa automática mudou. Reaproveita esta conexão
    // única (em vez de o hook da tela abrir um socket próprio) e invalida a query — a tela do
    // colaborador passa a conhecer a janela nova sem esperar o refetch periódico.
    socket.on('pausas-automaticas:updated', () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pausasAutomaticas.vigentes });
    });

    socket.on('sessions:force-closed', (data: {
      carPlate: string;
      count: number;
      collaboratorNames: string[];
    }) => {
      const names = data.collaboratorNames.join(', ');
      toast.info(
        `${data.count} sessão(ões) encerrada(s) no veículo ${data.carPlate}: ${names}`,
        { autoClose: 6000 },
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [accessToken, queryClient]);
}
